import { useState } from 'react';
import {
  type Color,
  ColorArea,
  ColorSlider,
  ColorThumb,
  parseColor,
  SliderTrack
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { normalizeHex } from './ColorPicker.utils';

const hsbOf = (value: string): Color =>
  parseColor(normalizeHex(value) ?? '#000000').toFormat('hsb');

export type ColorPickerSpectrumProps = {
  value: string;
  /** Every move of a thumb, as hex. */
  onChange: (hex: string) => void;
  disabled?: boolean;
};

/**
 * Any colour: the saturation and brightness area over the hue slider (React Aria's `ColorArea`
 * and `ColorSlider`), both draggable and moved with the arrow keys.
 */
export function ColorPickerSpectrum({
  value,
  onChange,
  disabled = false
}: ColorPickerSpectrumProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ColorPicker' });
  // its own colour, so a grey keeps the hue the slider is at; a value from elsewhere replaces it
  const [colour, setColour] = useState(() => hsbOf(value));
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setColour(hsbOf(value));
  }
  const change = (next: Color) => {
    const hex = next.toString('hex');
    setColour(next);
    setSynced(hex);
    onChange(hex);
  };
  return (
    <div className="flex flex-col gap-3">
      <ColorArea
        aria-label={t('areaLabel')}
        value={colour}
        onChange={change}
        isDisabled={disabled}
        xChannel="saturation"
        yChannel="brightness"
        className="h-36 w-full shrink-0 rounded-md data-disabled:opacity-50"
      >
        <ColorThumb className="size-5 rounded-full border-2 border-white shadow-sm ring-1 ring-black/40 data-dragging:size-6 data-focus-visible:size-6 data-focus-visible:ring-2 data-focus-visible:ring-brand-500" />
      </ColorArea>
      <ColorSlider
        aria-label={t('hueLabel')}
        channel="hue"
        value={colour}
        onChange={change}
        isDisabled={disabled}
        className="w-full data-disabled:opacity-50"
      >
        <SliderTrack className="h-4 w-full rounded-full">
          <ColorThumb className="top-1/2 size-5 rounded-full border-2 border-white shadow-sm ring-1 ring-black/40 data-dragging:size-6 data-focus-visible:size-6 data-focus-visible:ring-2 data-focus-visible:ring-brand-500" />
        </SliderTrack>
      </ColorSlider>
    </div>
  );
}
